import React, { useCallback, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Loading,
  Message,
  toast,
} from '@workwhile/ui'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import { ModalAsyncSelect, ModalWithSelect } from 'components/ModalSelect'
import {
  addWorkerCredentialFlowSchema,
  AddWorkerCredentialFlowSchema,
} from '../schemas/addWorkerCredential'
import { useAddWorkerCredentialFlowContextValue } from '../context/AddWorkerCredentialFlowProvider'
import { useUserWorkProfileWorkflowsContextValue } from '../../../context/UserWorkProfileWorkflowsProvider'

type RequirementOption = { label: string; value: number }

const fuzzyOptions: FullOptions<RequirementOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

type AddWorkerCredentialFlowMainProps = {
  userId: number
  onSuccess?: () => void
}

export const AddWorkerCredentialFlowMain = ({
  userId,
  onSuccess,
}: AddWorkerCredentialFlowMainProps) => {
  const {
    addWorkerCredentialFlow: { isOpen, close },
  } = useUserWorkProfileWorkflowsContextValue()

  const {
    state: {
      requirements,
      requirementsState: {
        isLoading: isRequirementsLoading,
        isError: isRequirementsError,
      },
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useAddWorkerCredentialFlowContextValue()

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<AddWorkerCredentialFlowSchema>({
    resolver: zodResolver(addWorkerCredentialFlowSchema),
    defaultValues: {
      workerRequirementId: undefined,
    },
  })

  const requirementOptions = useMemo(() => {
    return requirements.map((requirement) => ({
      label: requirement.displayName,
      value: requirement.id,
    }))
  }, [requirements])

  const searcher = useMemo(() => {
    return new Searcher<RequirementOption, FullOptions<RequirementOption>>(
      requirementOptions,
      fuzzyOptions
    )
  }, [requirementOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) {
        return Promise.resolve(requirementOptions)
      }

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<RequirementOption>[]

      return Promise.resolve(results)
    },
    [requirementOptions, searcher]
  )

  useEffect(() => {
    if (!isOpen) return

    reset({
      workerRequirementId: undefined,
    })
  }, [isOpen, reset])

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        userId,
        workerRequirementId: data.workerRequirementId,
      },
      {
        onSuccess: () => {
          toast.success('Credential added successfully', {
            position: 'top-center',
          })
          onSuccess?.()
          handleClose()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Add
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Add credential
        </Heading>

        <Box mt={4}>
          {isRequirementsLoading ? <Loading /> : null}
          {isRequirementsError ? (
            <Message variant="error" borderRadius="small">
              Failed to load credential options. Please try again.
            </Message>
          ) : null}
          {!isRequirementsLoading && !isRequirementsError ? (
            <Controller
              control={control}
              name="workerRequirementId"
              render={({ field, fieldState }) => (
                <Field
                  label="Credential"
                  error={
                    fieldState.error?.message?.toString() ||
                    errors.workerRequirementId?.message?.toString()
                  }
                >
                  <ModalAsyncSelect
                    block
                    isClearable
                    isSearchable
                    placeholder="Search by display name..."
                    defaultOptions={requirementOptions}
                    loadOptions={loadOptions}
                    value={
                      requirementOptions.find(
                        (option) => option.value === field.value
                      ) || null
                    }
                    onChange={(option) => {
                      field.onChange(option?.value ?? undefined)
                    }}
                  />
                </Field>
              )}
            />
          ) : null}
        </Box>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </ModalWithSelect>
  )
}
